package com.arena_web.ui.theme

/**
 * Icon catalog based on Figma design system.
 *
 * All SVG icons should be exported from Figma and placed in:
 *   site/src/jsMain/resources/public/icons/
 *
 * Usage:
 * ```
 * Image(src = AppIcons.Search, modifier = Modifier.size(24.px))
 * ```
 */
object AppIcons {

    private const val BASE_PATH = "/icons"
    private const val IMAGE_PATH = "/images"

    private fun icon(name: String): String = "$BASE_PATH/$name"
    private fun image(name: String): String = "$IMAGE_PATH/$name"

    // =========================================================================
    // Navigation / UI — 24x24
    // =========================================================================
    val Profile = icon("ic_profile.svg")
    val ProfileMuted = icon("ic_profile_muted.svg")
    val Globe = icon("ic_globe.svg")
    val Radio = icon("ic_radio.svg")
    val Chat = icon("ic_chat.svg")
    val ChatBubble = icon("ic_chat_bubble.svg")
    val RejectButton = icon("ic_reject_button.svg")
    val ConfirmButton = icon("ic_confirm_button.svg")
    val Notifications = icon("ic_notifications.svg")
    val Settings = icon("ic_settings.svg")
    val SettingsAlt = icon("ic_settings_alt.svg")
    val Search = icon("ic_search.svg")
    val ArrowsDiagonal = icon("ic_arrows_diagonal.svg")
    val Pencil = icon("ic_pencil.svg")
    val Map = icon("ic_map.svg")
    val GridFilled = icon("ic_grid_filled.svg")
    val RadarHex = icon("ic_radar_hex.svg")
    val StatLightning = icon("ic_stat_lightning.svg")
    val StatTarget = icon("ic_stat_target.svg")
    // Stat-block-specific (Figma 7904:364601 — clean target without the
    // arrow / cursor element). `StatTarget` keeps the legacy crosshair art
    // used elsewhere in the app.
    val StatBlockTarget = icon("ic_stat_block_target.svg")
    val StatBlockBall = icon("ic_stat_block_ball.svg")
    val StatBattery = icon("ic_stat_battery.svg")
    val StatFlame = icon("ic_stat_flame.svg")
    val StatGrip = icon("ic_stat_grip.svg")
    val StatMuscle = icon("ic_stat_muscle.svg")
    val CardBackUnderline = icon("ic_card_back_underline.svg")
    val CardFlip = icon("ic_card_flip.svg")
    val Lightning = icon("ic_lightning.svg")
    val ScreenFull = icon("ic_screen_full.svg")
    val Statistics = icon("ic_statistics.svg")
    val People = icon("ic_people.svg")
    val Videocam = icon("ic_videocam.svg")
    val ShieldHalf = icon("ic_shield_half.svg")
    val Check = icon("ic_check.svg")
    // Figma 11287:211176 — Pro Play / Arena brand mark used in the header.
    // Three pieces so the header can stack the small "Pro Play" label on top
    // of the wordmark while the glyph sits to the left.
    val ProPlayLogo = icon("ic_proplay_logo.svg")
    val ProPlayArenaWordmark = icon("ic_proplay_arena_text.svg")
    val ProPlayLabel = icon("ic_proplay_pro_play_text.svg")
    val Filter = icon("ic_filter.svg")
    val Add = icon("ic_add.svg")
    val AddSecondary = icon("ic_add_secondary.svg")
    val Diamond = icon("ic_diamond.svg")
    val Remove = icon("ic_remove.svg")
    val Star = icon("ic_star.svg")
    val ChevronDown = icon("ic_chevron_down.svg")
    val ChevronForward = icon("ic_chevron_forward.svg")
    val ChevronBack = icon("ic_chevron_back.svg")
    val Pause = icon("ic_pause.svg")
    val PauseSmall = icon("ic_pause_small.svg")
    val Play = icon("ic_play.svg")
    val Analytics = icon("ic_analytics.svg")
    val ArrowUp = icon("ic_arrow_up.svg")
    val EllipsisVertical = icon("ic_ellipsis_vertical.svg")
    val DragHandle = icon("ic_drag_handle.svg")
    val Heart = icon("ic_heart.svg")
    val Envelope = icon("ic_envelope.svg")
    val Verified = icon("ic_verified.svg")
    val Trash = icon("ic_trash.svg")
    val Sight = icon("ic_sight.svg")
    val Eye = icon("ic_eye.svg")
    val Expand = icon("ic_expand.svg")
    val ImageOutline = icon("ic_image_outline.svg")
    val ExpandProfile = icon("ic_expand_profile.svg")
    val Phone = icon("ic_phone.svg")
    val Attachment = icon("ic_attachment.svg")
    val Camera = icon("ic_camera.svg")
    val Emoji = icon("ic_emoji.svg")
    val Send = icon("ic_send.svg")
    val Pin = icon("ic_pin.svg")
    val Document = icon("ic_document.svg")
    val Mic = icon("ic_mic.svg")
    val Sparkle = icon("ic_sparkle.svg")
    val AiSparkle = icon("ic_ai_sparkle.svg")
    val Close = icon("ic_close.svg")
    val Dashboard = icon("ic_dashboard.svg")
    val Apps = icon("ic_apps.svg")
    val Menu = icon("ic_menu.svg")
    val TrendingUp = icon("ic_trending_up.svg")
    val Dollar = icon("ic_dollar.svg")
    val LockPeople = icon("ic_lock_people.svg")
    // White padlock with the blue keyhole — locked "Generate (Pro)" CTA
    // (mobile ic_lock_pro, Figma 11516:212444).
    val LockPro = icon("ic_lock_pro.svg")
    val LockChat = icon("ic_lock_chat.svg")
    val Wallet = icon("ic_wallet.svg")
    val ArrowForward = icon("ic_arrow_forward.svg")
    val ShieldCheckmark = icon("ic_shield_checkmark.svg")
    val Exit = icon("ic_exit.svg")

    // Focus & Activities — Activities and Lifestyle interest chips
    val Cycling = icon("ic_cycling.svg")
    val Gym = icon("ic_gym.svg")
    val Travel = icon("ic_travel.svg")
    val Camping = icon("ic_camping.svg")
    val Fishing = icon("ic_fishing.svg")
    val Music = icon("ic_music.svg")
    val Reading = icon("ic_reading.svg")
    val Art = icon("ic_art.svg")
    val Photography = icon("ic_photography.svg")
    val Cooking = icon("ic_cooking.svg")

    // =========================================================================
    // Zodiac signs (Figma 7640:188998)
    // =========================================================================
    val ZodiacAries = icon("ic_zodiac_aries.svg")
    val ZodiacTaurus = icon("ic_zodiac_taurus.svg")
    val ZodiacGemini = icon("ic_zodiac_gemini.svg")
    val ZodiacCancer = icon("ic_zodiac_cancer.svg")
    val ZodiacLeo = icon("ic_zodiac_leo.svg")
    val ZodiacVirgo = icon("ic_zodiac_virgo.svg")
    val ZodiacLibra = icon("ic_zodiac_libra.svg")
    val ZodiacScorpio = icon("ic_zodiac_scorpio.svg")
    val ZodiacSagittarius = icon("ic_zodiac_sagittarius.svg")
    val ZodiacCapricorn = icon("ic_zodiac_capricorn.svg")
    val ZodiacAquarius = icon("ic_zodiac_aquarius.svg")
    val ZodiacPisces = icon("ic_zodiac_pisces.svg")

    // =========================================================================
    // Auth provider icons
    // =========================================================================
    val Google = icon("ic_google.svg")
    val Apple = icon("ic_apple.svg")

    // Club contact actions (Figma 11475:424824 club page) — full-colour marks.
    val Instagram = icon("ic_instagram.svg")
    val GoogleMaps = icon("ic_google_maps.svg")
    val CallFilled = icon("ic_call_filled.svg")
    val HeartOutline = icon("ic_heart_outline.svg")
    val GlobeFilled = icon("ic_globe_filled.svg")

    // =========================================================================
    // Sport-specific — 24x24
    // =========================================================================
    val Calendar = icon("ic_calendar.svg")
    val Time = icon("ic_time.svg")
    val Shield = icon("ic_shield.svg")
    val FootballField = icon("ic_football_field.svg")
    val Card = icon("ic_card.svg")
    val Cart = icon("ic_cart.svg")
    val PlayerHub = icon("ic_player_hub.svg")
    val Basketball = icon("ic_basketball.svg")
    val Volleyball = icon("ic_volleyball.svg")
    val Tennis = icon("ic_tennis.svg")
    val Padel = icon("ic_padel.svg")

    // Mobile sport-dropdown glyphs — match the new Figma 10702:203681 design
    // (round soccer ball, segmented basketball, racquet outline, etc.). Stay
    // separate from the line-art `Basketball`/`Tennis`/`Padel`/`Volleyball`
    // family above which is used elsewhere in the app.
    val SportFootball = icon("ic_sport_football.png")
    val SportBasketball = icon("ic_sport_basketball.png")
    val SportVolleyball = icon("ic_sport_volleyball.png")
    val SportTennis = icon("ic_sport_tennis.png")
    val SportPadel = icon("ic_sport_padel.png")
    val SportOther = icon("ic_sport_other.svg")
    // Monochrome glyphs for the custom sports under "Other" (tinted via mask).
    val OtherKarting = icon("ic_other_karting.svg")
    val OtherBowling = icon("ic_other_bowling.svg")
    val OtherClimbing = icon("ic_other_climbing.svg")
    val OtherGolf = icon("ic_other_golf.svg")
    val OtherBilliards = icon("ic_other_billiards.svg")
    val OtherLaserTag = icon("ic_other_lasertag.svg")
    val OtherPaintball = icon("ic_other_paintball.svg")
    val OtherTableTennis = icon("ic_other_tabletennis.svg")
    val OtherAll = icon("ic_other_all.svg")
    val Trophy = icon("ic_trophy.svg")
    val InfoCircle = icon("ic_info_circle.svg")
    val Information = icon("ic_information.svg")
    val TennisField = icon("ic_tennis_field.svg")
    val ArrowUpOutline = icon("ic_arrow_up_outline.svg")
    val Enter = icon("enter.svg")
    val Location = icon("ic_location.svg")

    // =========================================================================
    // Arrows — various sizes
    // =========================================================================
    val ArrowRight = icon("ic_arrow_right.svg")
    val ArrowLeft = icon("ic_arrow_left.svg")
    val ArrowDown = icon("ic_arrow_down.svg")
    val ArrowRightLarge = icon("ic_arrow_right_large.svg")
    val ArrowLeftLarge = icon("ic_arrow_left_large.svg")

    // =========================================================================
    // Ball icons — large (24x24) & small (17x17)
    // =========================================================================
    val BallFootballLarge = icon("ic_ball_football_large.svg")
    val BallFootballSmall = icon("ic_ball_football_small.svg")
    val BallTennisLargeBlack = icon("ic_ball_tennis_large_black.svg")
    val BallTennisLargeWhite = icon("ic_ball_tennis_large_white.svg")
    val BallTennisSmall = icon("ic_ball_tennis_small.svg")
    val BallPadelLargeBlack = icon("ic_ball_padel_large_black.svg")
    val BallPadelLargeWhite = icon("ic_ball_padel_large_white.svg")
    val BallPadelSmall = icon("ic_ball_padel_small.svg")
    val BallVolleyballLarge = icon("ic_ball_volleyball_large.svg")
    val BallVolleyballSmall = icon("ic_ball_volleyball_small.svg")
    val BallBasketballLarge = icon("ic_ball_basketball_large.svg")
    val BallBasketballSmall = icon("ic_ball_basketball_small.svg")
    val BallFootballLargeWhite = icon("ic_ball_football_large_white.svg")
    val BallVolleyballLargeWhite = icon("ic_ball_volleyball_large_white.svg")
    val BallBasketballLargeWhite = icon("ic_ball_basketball_large_white.svg")

    // =========================================================================
    // My Activity icons — per sport (27x24)
    // =========================================================================
    val ActivityInactive = icon("ic_activity_inactive.svg")
    val ActivityFootball = icon("ic_activity_football.svg")
    val ActivityTennis = icon("ic_activity_tennis.svg")
    val ActivityVolleyball = icon("ic_activity_volleyball.svg")
    val ActivityBasketball = icon("ic_activity_basketball.svg")
    val ActivityPadel = icon("ic_activity_padel.svg")

    // =========================================================================
    // Activity quantity
    // =========================================================================
    val ActivityNumber = icon("ic_activity_number.svg")
    val ActivityNone = icon("ic_activity_none.svg")
    val Pulse = icon("ic_pulse.svg")
    val Date = icon("ic_date.svg")
    val Geo = icon("ic_geo.svg")

    // =========================================================================
    // Stat icons — 24x24
    // =========================================================================
    val StatWin = icon("ic_stat_win.svg")
    val StatDraw = icon("ic_stat_draw.svg")
    val StatLoss = icon("ic_stat_loss.svg")
    val StatLossRed = icon("ic_stat_loss_red.svg")
    val BarChart = icon("ic_bar_chart.svg")

    // =========================================================================
    // Overlay result decorations
    // =========================================================================
    val OverlayResultWin = icon("ic_overlay_result_win.svg")
    val OverlayResultDraw = icon("ic_overlay_result_draw.svg")
    val OverlayResultLoss = icon("ic_overlay_result_loss.svg")

    // =========================================================================
    // Cup / Trophy icons — 20x30
    // =========================================================================
    val CupGoldStrikers = icon("ic_cup_gold_strikers.svg")
    val CupSilverStrikers = icon("ic_cup_silver_strikers.svg")
    val CupBronzeStrikers = icon("ic_cup_bronze_strikers.svg")
    val CupGoldAssistants = icon("ic_cup_gold_assistants.svg")
    val CupSilverAssistants = icon("ic_cup_silver_assistants.svg")
    val CupBronzeAssistants = icon("ic_cup_bronze_assistants.svg")

    // =========================================================================
    // Empty state illustrations
    // =========================================================================
    val EmptyAvatar = icon("ic_empty_avatar.svg")
    val DemoPlayer = icon("ic_demo_player.png")
    val DemoCardFrame = icon("ic_demo_card_frame.png")
    val DemoRatingBox = icon("ic_demo_rating_box.png")
    val EmptyTopAssistants = icon("ic_empty_top_assistants.svg")
    val EmptyTopStrikers = icon("ic_empty_top_strikers.svg")
    val EmptyImagesStack = icon("ic_images_stack.svg")
    val EmptyCalendarLarge = icon("ic_calendar_large.svg")
    val EmptyLikes = icon("ic_empty_likes.svg")
    val EmptyMatches = icon("ic_empty_matches.svg")
    val EmptyChats = icon("ic_empty_chats.svg")
    // Follow list empty states (mobile sheets)
    val EmptyFollowers = icon("ic_empty_followers.svg")
    val EmptyFollowingPerson = icon("ic_empty_following_person.svg")
    val EmptyFollowingAdd = icon("ic_empty_following_add.svg")
    val DiscoverArrow = icon("ic_discover_arrow.svg")

    // =========================================================================
    // Logo / Stat icons
    // =========================================================================
    val LogoStat = icon("ic_logo_stat.svg")
    val TeamPeople = icon("ic_team_people.svg")

    // =========================================================================
    // Team icon variants — 42.9x30.1
    // =========================================================================
    val TeamIcon1 = icon("ic_team_1.svg")
    val TeamIcon2 = icon("ic_team_2.svg")
    val TeamIcon3 = icon("ic_team_3.svg")

    // =========================================================================
    // Team avatar circle assets
    // =========================================================================
    val TeamAvatarBg = icon("img_team_bg.png")
    val TeamAvatarEllipse = icon("img_team_ellipse.svg")
    val TeamAvatarGlowBlue = icon("img_team_glow_blue.svg")
    val TeamAvatarGlowDark = icon("img_team_glow_dark.svg")

    // Card background effects
    val BgShadowCard = icon("bg_shadow_card.svg")

    // Demo assets for player cards
    val DemoFlag = icon("img_flag_demo.png")
    val DemoTeamEmblem = icon("img_team_emblem_demo.png")

    // Player card status assets
    val VerifiedBadge = icon("ic_verified_badge.png")
    val BtnConfirm = icon("ic_btn_confirm.svg")
    val BtnReject = icon("ic_btn_reject.svg")
    val BtnDelete = icon("ic_btn_delete.svg")
    // Symbol-only marks (no pill background) — used when the pill is drawn
    // separately via CSS so the symbol can stay at its natural aspect while
    // the pill stretches independently.
    val CheckMark = icon("ic_check_mark.svg")
    val CrossMark = icon("ic_cross_mark.svg")

    // =========================================================================
    // Business / Schedule
    // =========================================================================
    val AlertExclamation = icon("ic_alert_exclamation.svg")
    val DollarSign = icon("ic_dollar_sign.svg")
    val StarRating24 = icon("ic_star_rating_24.svg")
    val StarRating16 = icon("ic_star_rating_16.svg")
    val StarOutline = icon("ic_star_outline.svg")
    val TimeBusiness = icon("ic_time_business.svg")
    val TrendingUpBlue = icon("ic_trending_up_blue.svg")
    val ModalTime = icon("ic_modal_time.svg")
    val ModalDollar = icon("ic_modal_dollar.svg")
    val ModalClose = icon("ic_modal_close.svg")
    val ModalCheck = icon("ic_modal_check.svg")
    val ModalCheckCircle = icon("ic_modal_check_circle.svg")

    // =========================================================================
    // Segmented Control / Profile tabs
    // =========================================================================
    val Ribbon = icon("ic_ribbon.svg")
    val Newspaper = icon("ic_newspaper.svg")

    // News page mode toggle — football24-style line icons: house = news
    // home, trophy cup = matches ("Прогнозы"-tab style). *Filled variants
    // render the selected state.
    val NewsHome = icon("ic_news_home.svg")
    // ?v=2 — the glyph changed (arched-door house); icons ship with a
    // 30-day immutable cache, so returning browsers need a new URL to see it.
    val NewsHomeFilled = icon("ic_news_home_filled.svg?v=2")
    val NewsMatches = icon("ic_news_matches.svg")
    val NewsMatchesFilled = icon("ic_news_matches_filled.svg")
    val Cards = icon("ic_cards.svg")
    val Pricetags = icon("ic_pricetags.svg")
    val Reader = icon("ic_reader.svg")
    val Copy = icon("ic_copy.svg")

    /** Multi-photo indicator overlaid on profile posts-grid tiles whose
     *  parent post has more than one image. Figma node 9300:142883 — the
     *  Ionicons "filled / copy" glyph rotated 180°, so the second sheet
     *  peeks from the bottom-left rather than top-left. */
    val PostMulti = icon("ic_post_multi.svg")

    // =========================================================================
    // Club Services / Amenities
    // =========================================================================
    val Parking = icon("ic_parking.svg")
    val Cafe = icon("ic_cafe.svg")
    val Wifi = icon("ic_wifi.svg")
    val Reception = icon("ic_reception.svg")
    val Shower = icon("ic_shower.svg")
    val FirstAid = icon("ic_first_aid.svg")

    // =========================================================================
    // Images
    // =========================================================================
    val TeamBackground = icon("image_football_team.png")
    val StatisticBackground = icon("Image_football_statistic.png")
    val EventFieldBackground = icon("event_football_field.png")

    fun statisticBackground(sport: String): String = when (sport.lowercase()) {
        "basketball" -> icon("image_basketball_statistic.png")
        "padel" -> icon("image_padel_statistic.png")
        "tennis" -> icon("image_tennis_statistic.png")
        "volleyball" -> icon("image_volleyball_statistic.png")
        else -> icon("Image_football_statistic.png")
    }

    // Sport-specific pitch/court art used as the IN_PROGRESS event page
    // background. `other` and any unknown sport fall back to the football
    // pitch so the layout never lands without a background.
    fun eventFieldBackground(sport: String): String = when (sport.lowercase()) {
        "basketball" -> icon("bg_event_basketball.png")
        "padel" -> icon("bg_event_padel.png")
        "tennis" -> icon("bg_event_tennis.png")
        "volleyball" -> icon("bg_event_voleyball.png")
        else -> icon("event_football_field.png")
    }

    // Mobile-only ambient backdrop for the Arena (events) screen — layered
    // over the default body composite. Portrait 1086×1448 per-sport art,
    // mirrored from the KMP app's composeResources drawables.
    fun mobileEventBackground(sport: String): String = when (sport.lowercase()) {
        "basketball" -> image("bg_basketball_event_mobile.png")
        "padel" -> image("bg_padel_event_mobile.png")
        "tennis" -> image("bg_tennis_event_mobile.png")
        "volleyball" -> image("bg_volleyball_event_mobile.png")
        else -> image("bg_football_event_mobile.png")
    }

    // =========================================================================
    // Helper: stat icon for a sport-profile attribute (back-of-card stats).
    //
    // Driven by the standardized attribute set seeded in V95:
    //   Striking, Accuracy, Physical, Dribbling, Defense, plus a
    //   sport-specific 6th — Possession (Football, Basketball) or Serve
    //   (Tennis, Volleyball, Padel). Possession and Serve are mutually
    //   exclusive per sport, so they can share the Flame icon without
    //   colliding on the same card.
    //
    // The arg can be the full attribute name (`"Striking"`) or its
    // 3-letter code (`"STR"`) — we normalize and match on prefix.
    // =========================================================================
    fun statIconFor(attribute: String): String {
        val key = attribute.trim().uppercase()
        return when {
            key.startsWith("STR") -> StatLightning   // Striking — power
            key.startsWith("ACC") -> StatTarget      // Accuracy — precision
            key.startsWith("PHY") -> StatMuscle      // Physical — strength
            key.startsWith("DRI") -> StatGrip        // Dribbling — control
            key.startsWith("DEF") -> StatBattery     // Defense — endurance
            key.startsWith("POS") -> StatFlame       // Possession — initiative
            key.startsWith("SER") -> StatFlame       // Serve — impact
            else -> StatTarget                       // safe fallback
        }
    }

    // =========================================================================
    // Helper: get ball icon for sport
    // =========================================================================
    fun ballLarge(sport: String): String = when (sport.lowercase()) {
        "football" -> BallFootballLarge
        "tennis" -> BallTennisLargeBlack
        "volleyball" -> BallVolleyballLarge
        "basketball" -> BallBasketballLarge
        "padel" -> BallPadelLargeBlack
        else -> BallFootballLarge
    }

    fun ballLargeWhite(sport: String): String = when (sport.lowercase()) {
        "football" -> BallFootballLargeWhite
        "tennis" -> BallTennisLargeWhite
        "volleyball" -> BallVolleyballLargeWhite
        "basketball" -> BallBasketballLargeWhite
        "padel" -> BallPadelLargeWhite
        else -> BallFootballLargeWhite
    }

    fun ballSmall(sport: String): String = when (sport.lowercase()) {
        "football" -> BallFootballSmall
        "tennis" -> BallTennisSmall
        "volleyball" -> BallVolleyballSmall
        "basketball" -> BallBasketballSmall
        "padel" -> BallPadelSmall
        else -> BallFootballSmall
    }

    fun activityIcon(sport: String): String = when (sport.lowercase()) {
        // Padel and Other share the Football palette → use the football icon to match
        "football", "padel", "other" -> ActivityFootball
        "tennis" -> ActivityTennis
        "volleyball" -> ActivityVolleyball
        "basketball" -> ActivityBasketball
        else -> ActivityInactive
    }

}
